import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { config } from '../config';

interface ApiErrorResponse {
  error: string;
  description?: string;
  reason?: string;
  organizationId?: string;
}

/** The `reason` the API returns when a removal would strip the org's owner. */
export const OWNER_TRANSFER_REQUIRED = 'owner_transfer_required';

/**
 * Reads the "you must hand ownership over first" signal off a failed request.
 *
 * Removing someone's last team membership detaches them from the organization,
 * and for the owner that is a trap — they would own a tenant they cannot enter,
 * and ownership is capped at one org per user so they could not start fresh.
 * The API answers 409 with this reason rather than a bare conflict so the caller
 * can offer the handover instead of just reporting a failure.
 */
export function isOwnerTransferRequired(err: unknown): boolean {
  const response = (err as AxiosError<ApiErrorResponse> | null)?.response;
  return response?.status === 409 && response.data.reason === OWNER_TRANSFER_REQUIRED;
}

/**
 * Reassigns an organization's owner. Requires `org:transfer_ownership`, which is
 * deliberately not implied by `org:update` — renaming an organization and
 * deciding who owns it are different authorities.
 *
 * The backend rejects a nominee who isn't a member of the organization, or who
 * already owns another one.
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { organizationId: string; newOwnerUserId: string }
  >({
    mutationFn: async ({ organizationId, newOwnerUserId }) => {
      await axios.put(
        `${config.backendApi}/api/organizations/${organizationId}/owner`,
        { newOwnerUserId },
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      // `ownedOrganizationId` gates the create-organization action for both the
      // outgoing and incoming owner, so the cached user is now stale.
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      void queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}
